<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { PSpaceSize } from '../space/types'
import LabelInputField from '../fields/components/LabelInputField.vue'
import PIcon from '../icon/PIcon.vue'
import PLoader from '../loader/PLoader.vue'
import PSpace from '../space/PSpace.vue'
import PCard from '../card/PCard.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'
import { PCardRound, PCardSize } from '../card/types'
import { PFileType, PFileVariant } from './types'

type Props = {
  variant: PFileVariant
  label?: null | string
  tooltip?: null | string
  hint?: null | string
  required: boolean
  loading: boolean
  cy: string
  acceptFiles?: PFileType[]
  multiple?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: PFileVariant.FILL,
  label: null,
  tooltip: null,
  hint: null,
  required: false,
  loading: false,
  acceptFiles: () => [],
  multiple: false,
})

const model = defineModel<File[] | null>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const showFileTypeError = ref(false)
let fileTypeErrorTimeoutRef: number | null

const acceptFileTypes = computed<string[]>(() => {
  const acceptFiles: string[] = []

  props.acceptFiles.map((accept: PFileType) => {
    switch (accept) {
      case PFileType.IMAGE:
        acceptFiles.push('image/*')
        break
      case PFileType.PDF:
        acceptFiles.push('application/pdf')
        break
      case PFileType.CSV:
        acceptFiles.push('text/csv')
        break
      case PFileType.JSON:
        acceptFiles.push('application/json')
        break
      case PFileType.XML:
        acceptFiles.push('application/xml')
        acceptFiles.push('text/xml')
        break
      case PFileType.GPC:
        acceptFiles.push('.gpc')
        break
      case PFileType.TXT:
        acceptFiles.push('text/plain')
        break
      case PFileType.WORD:
        acceptFiles.push('application/msword')
        acceptFiles.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document')
        acceptFiles.push('application/vnd.oasis.opendocument.text')
        break
      case PFileType.EXCEL:
        acceptFiles.push('application/vnd.ms-excel')
        acceptFiles.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        acceptFiles.push('application/vnd.oasis.opendocument.spreadsheet')
        break
    }
  })

  if (acceptFiles.length === 0) {
    acceptFiles.push('*/*')
  }

  return acceptFiles
})

const openFileExplorer = function () {
  if (props.loading) return
  fileInputRef.value?.click()
}

const setFilesFromList = function (files: FileList | null) {
  if (!files?.length) return
  let fileArray = Array.from(files)
  const hasRejected = fileArray.some((f) => !isFileAccepted(f))

  if (hasRejected && props.acceptFiles.length > 0 && !props.acceptFiles.includes(PFileType.OTHER)) {
    showFileTypeError.value = true
    clearTimeout(fileTypeErrorTimeoutRef ?? undefined)
    fileTypeErrorTimeoutRef = window.setTimeout(() => {
      showFileTypeError.value = false
    }, 2000)
    return
  }

  clearTimeout(fileTypeErrorTimeoutRef ?? undefined)

  showFileTypeError.value = false
  fileArray = fileArray.filter(isFileAccepted)

  if (!props.multiple && fileArray.length) {
    fileArray = [fileArray[0]]
  }
  if (fileArray.length) {
    model.value = fileArray
  }
}

const isFileAccepted = function (file: File): boolean {
  if (props.acceptFiles.length === 0 || props.acceptFiles.includes(PFileType.OTHER)) return true

  return acceptFileTypes.value.some((accept) => {
    if (accept.endsWith('/*')) {
      const [type] = accept.split('/')
      return file.type.startsWith(`${type}/`)
    }

    if (accept.startsWith('.')) {
      const extension = accept.split('.')
      const type = file.name.split('.')

      if (type.length < 2) {
        return false
      }

      return extension[extension.length - 1] === type[type.length - 1]?.toLowerCase()
    }

    return file.type === accept
  })
}

const onFileChange = function (evt: Event) {
  const target = evt.target as HTMLInputElement
  setFilesFromList(target.files)
  target.value = ''
}

const onDragOver = function (evt: DragEvent) {
  if (props.loading) return
  evt.preventDefault()
  evt.stopPropagation()
  if (evt.dataTransfer) {
    evt.dataTransfer.dropEffect = 'copy'
  }
  isDragging.value = true
}

const onDragLeave = function (evt: DragEvent) {
  evt.preventDefault()
  evt.stopPropagation()
  const relatedTarget = evt.relatedTarget as Node | null
  const currentTarget = evt.currentTarget as HTMLElement | null
  if (!relatedTarget || !currentTarget?.contains(relatedTarget)) {
    isDragging.value = false
    showFileTypeError.value = false
    clearTimeout(fileTypeErrorTimeoutRef ?? undefined)
    fileTypeErrorTimeoutRef = null
  }
}

const onDrop = function (evt: DragEvent) {
  if (props.loading) return
  evt.preventDefault()
  evt.stopPropagation()
  isDragging.value = false
  setFilesFromList(evt.dataTransfer?.files ?? null)
}

watch(model, (val) => {
  if (!val?.length && fileInputRef.value) {
    fileInputRef.value.value = ''
  }
})
</script>

<template>
  <div class="ds-file-upload" :class="{ 'p-cursor-pointer': !loading }" :data-cy="cy">
    <input
      ref="fileInputRef"
      type="file"
      class="ds-file-upload__input"
      :accept="acceptFileTypes.join(',') || undefined"
      :multiple="multiple"
      @change="onFileChange"
    />
    <label-input-field :label="label" :tooltip="tooltip" :is-required="required" />

    <p-card
      class="ds-file-upload__card p-transition-duration"
      :class="{
        'ds-file-upload__card--square': variant === PFileVariant.SQUARE,
        'ds-file-upload__card--fill': variant === PFileVariant.FILL,
        'ds-file-upload__card--dragging': isDragging && !loading,
        'ds-file-upload__card--error': showFileTypeError,
      }"
      :round="PCardRound.MD"
      :pa-mobile="PCardSize.SM"
      @click="openFileExplorer"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <div
        class="ds-file-upload__card-content p-text-center p-flex-1"
        :class="{ 'ds-file-upload__card-content--hidden': loading }"
      >
        <template v-if="showFileTypeError">
          <p-icon icon="circle-exclamation" :color="Color.NEGATIVE" />
          <p-space :mobile="PSpaceSize.XS" vertical />
          <p-text-description :text="$t('File type not allowed')" :color="Color.NEGATIVE" />
        </template>
        <template v-else>
          <p-icon :icon="isDragging ? 'file-import' : 'plus-circle'" :color="Color.GREY_500" />
          <p-space :mobile="PSpaceSize.XS" vertical />
          <p-text-description
            :text="isDragging ? $t('Drop file') : $t('Upload or drag&drop file')"
            :color="Color.GREY_500"
          />
        </template>
      </div>
      <div v-if="loading" class="ds-file-upload__loader">
        <p-loader :size="44" :color="Color.PRIMARY" />
      </div>
    </p-card>

    <template v-if="hint">
      <p-space :mobile="PSpaceSize.XS" vertical />
      <p-text-description :text="hint" :color="Color.GREY_500" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/_brand.module.scss';
@import '../../assets/scss/shared/variables/ds.spacing';

$square-size: 128px;

.ds-file-upload {
  &__input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__card {
    position: relative;
    display: flex;
    flex-direction: column;
    height: $square-size;
    width: 100%;

    border: 2px dashed getColor('grey300');

    &:hover {
      border-color: getColor('grey400');
    }

    &--dragging {
      border-color: getColor('primary');
      background: getColor('grey50') !important;
    }

    &--error {
      border-color: getColor('negative');
      background: getColor('negativeTransparent') !important;
    }

    &--square {
      max-width: $square-size;
    }
  }

  &__card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
  }
}
</style>
